import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { useState } from 'react'
import { Mic, Video, Monitor, Radio, ChevronDown, ChevronUp } from '@tamagui/lucide-icons'
import { useAppStore, useServerStore } from '@/store'
import { TIER_CONFIGS, serverBilling, users } from '@/mocks'
import { TierBadge } from '../../common'
import { UsageEstimator } from './UsageEstimator'
import type { ServerTier, TierConfig } from '@/types'

const Container = styled(View, { flex: 1, flexDirection: 'column' })

const Header = styled(View, {
  paddingHorizontal: 32, paddingVertical: 20,
  borderBottomWidth: 1, borderBottomColor: '$borderDefault',
})

const Title = styled(Text, {
  fontFamily: '$fontHeading', fontWeight: '600', fontSize: 20, color: '$surfaceForeground',
})

const Body = styled(View, { padding: 32, gap: 24, maxWidth: 700 })

const Card = styled(View, {
  backgroundColor: '$surfaceRaised', borderRadius: '$borderRadiusXl', borderWidth: 1, borderColor: '$borderDefault', padding: 16, gap: 12,
})

const SectionTitle = styled(Text, {
  fontFamily: '$fontBody', fontSize: 12, fontWeight: '600',
  color: '$color8', textTransform: 'uppercase', letterSpacing: 0.5,
})

const Label = styled(Text, {
  fontFamily: '$fontBody', fontSize: 14, color: '$surfaceForeground',
})

const Muted = styled(Text, {
  fontFamily: '$fontBody', fontSize: 13, color: '$sidebarMuted',
})

const SmallMuted = styled(Text, {
  fontFamily: '$fontBody', fontSize: 12, color: '$color8',
})

const ActionButton = styled(View, {
  height: 32, paddingHorizontal: 16, borderRadius: '$borderRadiusMd',
  alignItems: 'center', justifyContent: 'center', backgroundColor: '$accent',
  cursor: 'pointer', alignSelf: 'flex-start',
  hoverStyle: { opacity: 0.9 },
})

const ProgressBarOuter = styled(View, {
  height: 12, borderRadius: '$borderRadiusMd', backgroundColor: '$sidebarActive', overflow: 'hidden',
})

const UsageRow = styled(View, {
  flexDirection: 'row', alignItems: 'center', gap: 10, height: 28,
})

const TIER_ORDER: ServerTier[] = ['free', 'spark', 'blaze', 'forge', 'legion']

function getProgressColor(pct: number, accentVal: string): string {
  if (pct >= 90) return '#CF3E4F'
  if (pct >= 75) return '#E8A840'
  return accentVal
}

function getNextTier(current: ServerTier): ServerTier | null {
  const idx = TIER_ORDER.indexOf(current)
  return idx < TIER_ORDER.length - 1 ? TIER_ORDER[idx + 1] : null
}

function formatMH(n: number): string {
  return n.toLocaleString()
}

export function BillingSettings() {
  const { activeServerId } = useAppStore()
  const server = useServerStore((s) => activeServerId ? s.servers[activeServerId] : undefined)
  const theme = useTheme()
  const [refOpen, setRefOpen] = useState(false)

  if (!server || !activeServerId) return null

  const tier = server.tier
  const config = TIER_CONFIGS[tier]
  const billing = serverBilling[activeServerId]
  const usage = billing?.usage
  const fuelContributors = billing?.fuelContributors || []

  const usagePct = usage ? Math.round((usage.totalMH / config.mediaHours) * 100) : 0
  const progressColor = getProgressColor(usagePct, theme.accent?.val || '#3ECFCF')
  const nextTier = getNextTier(tier)
  const nextConfig = nextTier ? TIER_CONFIGS[nextTier] : null

  const fuelRevenue = fuelContributors.length * config.fuelPrice
  const fuelBonusMH = fuelContributors.length * 50
  const fuelBonusStorage = fuelContributors.length * 250 // MB

  // Estimate runout date
  const daysInMonth = 30
  const daysPassed = 15 // mock: mid-month
  const projectedTotal = usage ? Math.round((usage.totalMH / daysPassed) * daysInMonth) : 0
  const runoutDay = usage && usage.totalMH > 0
    ? Math.round((config.mediaHours / (usage.totalMH / daysPassed)))
    : null

  // Overage calculation
  const overageMH = usage ? Math.max(0, usage.totalMH - config.mediaHours) : 0
  const overageCharge = config.overageRate ? Math.min(overageMH * config.overageRate, config.overageCap || 0) : 0

  return (
    <Container>
      <Header><Title>Billing</Title></Header>
      <ScrollView flex={1}>
        <Body>
          {/* Plan Overview */}
          <Card>
            <View flexDirection="row" alignItems="center" justifyContent="space-between">
              <View flexDirection="row" alignItems="center" gap={10}>
                <TierBadge tier={tier} />
                <Label fontWeight="600" fontSize={16}>{config.label} Plan</Label>
              </View>
              {config.price > 0 && (
                <Label fontWeight="600">${config.price}/mo</Label>
              )}
            </View>
            <Muted>
              {config.price === 0 ? 'Free forever' : `Renews April 1, 2026`}
              {' · '}{formatMH(config.mediaHours)} Media Hours included
            </Muted>
            {server.isOwner && tier !== 'legion' && nextConfig && (
              <ActionButton>
                <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surface">
                  Upgrade to {nextConfig.label}
                </Text>
              </ActionButton>
            )}
          </Card>

          {/* Media Hours Usage */}
          {usage && (
            <Card>
              <SectionTitle>Media Hours Usage</SectionTitle>
              <View gap={4}>
                <View flexDirection="row" justifyContent="space-between">
                  <Label fontWeight="600">{formatMH(usage.totalMH)} / {formatMH(config.mediaHours)} MH</Label>
                  <Label fontWeight="600" color={progressColor}>{usagePct}%</Label>
                </View>
                <ProgressBarOuter>
                  <View
                    height="100%"
                    borderRadius="$borderRadiusMd"
                    backgroundColor={progressColor}
                    width={`${Math.min(usagePct, 100)}%`}
                    // @ts-expect-error -- web transition
                    style={{ transition: 'width 300ms ease' }}
                  />
                </ProgressBarOuter>
                {usagePct >= 75 && runoutDay && (
                  <SmallMuted color="#E8A840">
                    At this pace you'll run out around day {Math.min(runoutDay, daysInMonth)} of the billing cycle
                  </SmallMuted>
                )}
              </View>

              {/* Breakdown */}
              <View gap={4} marginTop={8}>
                <SectionTitle>Breakdown</SectionTitle>
                <UsageRow>
                  <Mic size={14} color="$sidebarMuted" />
                  <Label flex={1}>Voice</Label>
                  <SmallMuted>{formatMH(usage.voiceMH)} MH ({(usage.voiceMH * 60).toLocaleString()} min)</SmallMuted>
                </UsageRow>
                <UsageRow>
                  <Video size={14} color="$sidebarMuted" />
                  <Label flex={1}>Video</Label>
                  <SmallMuted>{formatMH(usage.videoMH)} MH ({(usage.videoMH * 2).toLocaleString()} min @ 720p)</SmallMuted>
                </UsageRow>
                <UsageRow>
                  <Monitor size={14} color="$sidebarMuted" />
                  <Label flex={1}>Screen Share</Label>
                  <SmallMuted>{formatMH(usage.screenShareMH)} MH ({(usage.screenShareMH * 1.5).toLocaleString()} min)</SmallMuted>
                </UsageRow>
                <UsageRow>
                  <Radio size={14} color="$sidebarMuted" />
                  <Label flex={1}>Go Live</Label>
                  <SmallMuted>{formatMH(usage.goLiveMH)} MH</SmallMuted>
                </UsageRow>
              </View>

              {/* Quick reference toggle */}
              <View
                flexDirection="row"
                alignItems="center"
                gap={6}
                cursor="pointer"
                onPress={() => setRefOpen(!refOpen)}
                marginTop={4}
              >
                {refOpen ? <ChevronUp size={14} color="$accent" /> : <ChevronDown size={14} color="$accent" />}
                <Text fontFamily="$fontBody" fontSize={12} color="$accent">Quick reference</Text>
              </View>
              {refOpen && (
                <View gap={2} paddingLeft={20}>
                  <SmallMuted>1 MH = 60 min voice per person</SmallMuted>
                  <SmallMuted>1 MH = 2 min video per person (720p)</SmallMuted>
                  <SmallMuted>1 MH = 1.5 min screen share per viewer</SmallMuted>
                  <SmallMuted>1 MH = 1 min Go Live per viewer</SmallMuted>
                </View>
              )}
            </Card>
          )}

          {/* Fuel Supporters */}
          {config.fuelEnabled && (
            <Card
              borderColor="#E8A840"
              backgroundColor="rgba(232, 168, 64, 0.04)"
            >
              <View flexDirection="row" alignItems="center" gap={8}>
                <Text fontSize={16}>⚔️</Text>
                <SectionTitle color="#E8A840">Tribute Supporters</SectionTitle>
              </View>
              <View flexDirection="row" justifyContent="space-between">
                <Muted>{fuelContributors.length} supporter{fuelContributors.length !== 1 ? 's' : ''}</Muted>
                <Label fontWeight="600" color="#E8A840">${fuelRevenue}/mo</Label>
              </View>
              {fuelContributors.length > 0 && (
                <View gap={4}>
                  <SmallMuted>Bonus: +{fuelBonusMH} MH · +{fuelBonusStorage >= 1000 ? `${(fuelBonusStorage / 1000).toFixed(1)} GB` : `${fuelBonusStorage} MB`} storage</SmallMuted>
                  <View flexDirection="row" flexWrap="wrap" gap={6} marginTop={4}>
                    {fuelContributors.map((fc) => {
                      const u = users[fc.userId]
                      return (
                        <View
                          key={fc.userId}
                          backgroundColor="rgba(232, 168, 64, 0.1)"
                          borderRadius="$borderRadiusSm"
                          paddingHorizontal={8}
                          paddingVertical={3}
                        >
                          <Text fontFamily="$fontBody" fontSize={12} color="#E8A840">
                            {u ? u.displayName : fc.userId}
                          </Text>
                        </View>
                      )
                    })}
                  </View>
                </View>
              )}
            </Card>
          )}

          {/* Overage Status */}
          {config.overageRate !== null && (
            <Card>
              <SectionTitle>Overage Status</SectionTitle>
              {overageMH > 0 ? (
                <View gap={4}>
                  <Label>{formatMH(overageMH)} MH over limit</Label>
                  <Label fontWeight="600" color="$error">${overageCharge.toFixed(2)} charge (${config.overageRate}/MH, cap ${config.overageCap})</Label>
                </View>
              ) : (
                <Muted>No overages this period</Muted>
              )}
            </Card>
          )}

          {/* Upgrade Nudge */}
          {usagePct >= 75 && nextConfig && server.isOwner && (
            <View
              backgroundColor="rgba(232, 168, 64, 0.08)"
              borderWidth={1}
              borderColor="#E8A840"
              borderRadius="$borderRadiusXl"
              padding={16}
              gap={8}
            >
              <Label fontWeight="600" color="#E8A840">Consider upgrading</Label>
              <Muted>
                Your server is using {usagePct}% of its Media Hours. Upgrading to{' '}
                <Text fontWeight="600" color="$surfaceForeground">{nextConfig.label}</Text> gives you{' '}
                {formatMH(nextConfig.mediaHours)} MH/month for ${nextConfig.price}/mo.
              </Muted>
            </View>
          )}

          {/* Plan Comparison Table */}
          <View gap={8}>
            <SectionTitle>Compare Plans</SectionTitle>
            <View
              flexDirection="row"
              gap={0}
              overflow="hidden"
              borderRadius="$borderRadiusXl"
              borderWidth={1}
              borderColor="$borderDefault"
            >
              {TIER_ORDER.map((t) => {
                const c = TIER_CONFIGS[t]
                const isCurrent = t === tier
                return (
                  <View
                    key={t}
                    flex={1}
                    padding={12}
                    gap={8}
                    backgroundColor={isCurrent ? '$surfaceRaised' : '$surface'}
                    borderLeftWidth={t !== 'free' ? 1 : 0}
                    borderLeftColor="$borderDefault"
                    {...(isCurrent && {
                      borderWidth: 2,
                      borderColor: '$accent',
                      borderRadius: '$borderRadiusLg',
                    })}
                  >
                    <View alignItems="center" gap={4}>
                      <TierBadge tier={t} size="sm" />
                      <Label fontWeight="600" fontSize={16}>
                        {c.price === 0 ? 'Free' : `$${c.price}`}
                      </Label>
                      {c.price > 0 && <SmallMuted>/month</SmallMuted>}
                    </View>
                    <View gap={4}>
                      <PlanRow label="MH" value={formatMH(c.mediaHours)} />
                      <PlanRow label="Members" value={c.members.toLocaleString()} />
                      <PlanRow label="Video" value={c.video === false ? '—' : c.video} />
                      <PlanRow label="Storage" value={c.storage < 1 ? `${c.storage * 1000} MB` : `${c.storage} GB`} />
                      <PlanRow label="Emoji" value={c.customEmoji === 0 ? '—' : String(c.customEmoji)} />
                      <PlanRow label="History" value={c.messageHistory} />
                      <PlanRow label="Moderation" value={c.moderation} />
                      <PlanRow label="Bots" value={c.botSlots === null ? '∞' : c.botSlots === 0 ? '—' : String(c.botSlots)} />
                      <PlanRow label="Analytics" value={c.analytics} />
                    </View>
                  </View>
                )
              })}
            </View>
          </View>

          {/* Usage Estimator */}
          <UsageEstimator currentTier={tier} />
        </Body>
      </ScrollView>
    </Container>
  )
}

function PlanRow({ label, value }: { label: string; value: string }) {
  return (
    <View flexDirection="row" justifyContent="space-between">
      <SmallMuted>{label}</SmallMuted>
      <Text fontFamily="$fontBody" fontSize={11} color="$surfaceForeground">{value}</Text>
    </View>
  )
}
